import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { thumb } from '../assets/photos';
import Seo from '../components/Seo';

const Reveal = ({
  children, delay = 0, className = '', y = 24,
}: {
  children: React.ReactNode; delay?: number; className?: string; y?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Testimonials = () => {
  const testimonials = [
    {
      name:  'Keara & Alessio',
      venue: 'Rosemary Hill',
      date:  'June 2021',
      text:  'We would just like to say thank you so so much for everything you did for us on Saturday and for making the whole process so smooth and easy! You guided us through everything and made us feel so comfortable and relaxed! We will definitely recommend you to anyone and everyone! You have an absolutely incredible team and we are so grateful!',
      image: thumb('alessandra-gert/5.jpg'),
    },
    {
      name:  'Sarah & Michael',
      venue: 'The Greenhouse Cafe',
      date:  'March 2021',
      text:  'George & Co. captured our wedding day perfectly! From the engagement session to the big day, everything was seamless. The photos are absolutely stunning and the video brought tears to our eyes. We couldn\'t be happier with our choice!',
      image: thumb('sarah-simon/couple-5.jpg'),
    },
    {
      name:  'Emma & David',
      venue: 'Rosemary Hill',
      date:  'December 2020',
      text:  'The team at George & Co. went above and beyond our expectations. They made us feel so comfortable in front of the camera and captured every special moment. The final product exceeded our wildest dreams. Thank you for preserving our memories so beautifully!',
      image: thumb('mosa-kudzai/5.jpg'),
    },
    {
      name:  'Lisa & James',
      venue: 'Monate Game Lodge',
      date:  'October 2020',
      text:  'We were blown away by the quality of our wedding photos and video. George & Co. has an incredible eye for detail and knows exactly how to capture the perfect moments. The editing is flawless and the film is stunning. Highly recommend!',
      image: thumb('nastasha-lohan/5.jpg'),
    },
    {
      name:  'Rachel & Tom',
      venue: 'De Uijlenes Guest Farm',
      date:  'August 2020',
      text:  'Choosing George & Co. for our wedding was the best decision we made! They are not only incredibly talented photographers but also wonderful people. They made our day stress-free and captured every beautiful moment. The results are absolutely magical!',
      image: thumb('amber-erik/10.jpg'),
    },
    {
      name:  'Jessica & Mark',
      venue: 'Kleinkaap',
      date:  'May 2020',
      text:  'From our first meeting to receiving our final photos, George & Co. exceeded all our expectations. Their attention to detail is remarkable and they truly understand how to capture authentic moments. Our wedding photos are absolutely perfect!',
      image: thumb('dawn-henko/10.jpg'),
    },
  ];

  return (
    <div className="min-h-screen bg-ivory overflow-x-hidden">
      <Seo route="/testimonials" />

      {/* ── Page Header ─────────────────────────────────────────── */}
      <div className="page-header bg-ivory px-8 md:px-14 lg:px-20">
        <div className="max-w-screen-xl mx-auto">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="label-sm text-mist mb-5"
          >
            Couples' Stories
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="heading-display text-ink text-[clamp(3rem,7vw,7rem)] mb-6"
          >
            What they say
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="body-light max-w-lg"
          >
            These are the words that remind us why we do what we do.
          </motion.p>
        </div>
      </div>

      {/* ── Stats strip ─────────────────────────────────────────── */}
      <div className="bg-bone border-y border-silver/30">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { n: '500+', l: 'Happy couples' },
              { n: '11+',  l: 'Years experience' },
              { n: '35+',  l: 'Venues' },
              { n: '7+',   l: 'Countries' },
            ].map(({ n, l }) => (
              <div key={l}>
                <div className="heading-display text-ink text-3xl md:text-4xl mb-1">{n}</div>
                <p className="label-sm text-mist">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Featured quote ───────────────────────────────────────── */}
      <section className="bg-charcoal py-24 md:py-36">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <Reveal>
            <p className="label-sm text-champagne mb-12 md:mb-16">Featured</p>
          </Reveal>
          <Reveal delay={0.15} y={20}>
            <blockquote className="heading-italic text-white text-[clamp(1.75rem,4.5vw,4rem)] leading-[1.18] max-w-5xl mb-12 md:mb-14">
              "{testimonials[0].text}"
            </blockquote>
          </Reveal>
          <Reveal delay={0.3} y={12}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
              <div className="overflow-hidden w-12 h-12 flex-shrink-0">
                <img loading="lazy" decoding="async"
                  src={testimonials[0].image}
                  alt={testimonials[0].name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="label-sm text-white/60 mb-0.5">{testimonials[0].name}</p>
                <p className="label-sm text-white/60">{testimonials[0].venue} · {testimonials[0].date}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── All testimonials grid ────────────────────────────────── */}
      <section className="bg-ivory py-20 md:py-32">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <div className="divide-y divide-silver/40">
            {testimonials.slice(1).map((t, i) => (
              <Reveal key={t.name} delay={i * 0.07} y={16}>
                <div className="py-12 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">

                  {/* Image */}
                  <div className="md:col-span-3 overflow-hidden">
                    <div className="overflow-hidden aspect-[4/3]">
                      <img loading="lazy" decoding="async"
                        src={t.image}
                        alt={t.name}
                        className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-[1200ms] ease-out"
                      />
                    </div>
                  </div>

                  {/* Quote */}
                  <div className="md:col-span-9 flex flex-col justify-center">
                    <blockquote className="heading-italic text-ink text-[clamp(1.3rem,2.5vw,2.2rem)] leading-[1.35] mb-8">
                      "{t.text}"
                    </blockquote>
                    <div className="flex items-center gap-5">
                      <div className="w-px h-8 bg-silver flex-shrink-0" />
                      <div>
                        <p className="label-sm text-ink mb-0.5">{t.name}</p>
                        <p className="label-sm text-mist">{t.venue} · {t.date}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────── */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <Reveal>
            <h2 className="heading-display text-ink text-[clamp(2rem,4.5vw,4rem)]">
              Ready to write<br />
              <span className="heading-italic">your story?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/contact" className="link-underline text-ink">
              Begin your inquiry <span className="ml-1">→</span>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
